/** @shell standard @header titleMode=inline */
import React, { useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Image, Dimensions, StatusBar, Platform } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../navigation/types';
import { Clickable } from '../components/common/Clickable';
import { ScreenHeader } from '../components/common/ScreenHeader';
import { SvgIcon } from '../components/common/SvgIcon';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
} from '../shared/theme/sharedStyles';
import { useTranslation, getApiText, getApiName, formatDisplayDecimal } from '../shared/i18n';
import { horizontalLtrDirectionStyle, useRtl, type UseRtlResult } from '../shared/i18n/rtl';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useIosStatusBarStyle } from '../shared/navigation/useIosStatusBarStyle';
import { getOrganization } from '../data/domains/config';
import { FoodItemRecipeSection } from './components/FoodItemRecipeSection';
import { HealthInformationCmsGate } from '../healthInformation/components/HealthInformationCmsGate';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioCardShell,
  DASH_STUDIO_RADIUS,
} from '../shared/theme/studio/stackScreenShell';
import { DASH_STUDIO_INNER } from '../shared/theme/studio/dashboardConstants';
import { useMenuFlowVariant } from '../data/domains/config';
import { FoodItemDetailScreenSet2 } from './variants/set2/FoodItemDetailScreenSet2';
import { resolveWebLayoutWidth } from '../shared/platform/webLayout';

const SCREEN_WIDTH = resolveWebLayoutWidth(Dimensions.get('window').width);

type FoodItemDetailRouteProp = RouteProp<RootStackParamList, 'FoodItemDetail'>;

function normalizeItem(params: FoodItemDetailRouteProp['params'], name_fallback?: string) {
  const item = params?.item;
  if (!item || typeof item !== 'object') {
    return null;
  }
  const name = getApiName(item as Record<string, unknown>) || name_fallback || 'Food Item';
  const a_name = (item as any).a_name ?? '';
  const description = item.description ?? (item as any).a_description ?? undefined;
  const a_description = (item as any).a_description ?? '';
  const imageUri = item.attachment ?? (item as any).image ?? undefined;
  const cal = item.calories != null ? (typeof item.calories === 'string' ? parseFloat(item.calories) : Number(item.calories)) : 0;
  const protein = item.protein != null ? (typeof item.protein === 'string' ? parseFloat(item.protein) : Number(item.protein)) : 0;
  const carbs = item.carbs != null ? (typeof item.carbs === 'string' ? parseFloat(item.carbs) : Number(item.carbs)) : 0;
  const fats = item.fats != null ? (typeof item.fats === 'string' ? parseFloat(item.fats) : Number(item.fats)) : 0;
  const initialQty = typeof (item as any).quantity === 'number' ? (item as any).quantity : 0;
  const menu_builder_id = (item as any).menu_builder_id ?? (item as any).food_type_id ?? 0;
  return {
    id: (item as any).id,
    menu_builder_id,
    name,
    a_name,
    imageUri,
    description,
    a_description,
    calories: cal,
    protein,
    carbs,
    fats,
    initialQty,
    food_type_category: (item as any).food_type_category,
    recipe_text: (item as any).recipe_text,
    a_recipe_text: (item as any).a_recipe_text,
    recipes: (item as any).recipes,
    health_code: (item as any).health_code,
  };
}

function createFoodItemDetailStyles(c: ThemeColors, rtl: UseRtlResult) {
  const text_align_primary = rtl.textAlign;
  const writing_direction = rtl.writingDirection;
  const studio_card = {
    ...studioCardShell(c),
    borderRadius: DASH_STUDIO_RADIUS,
    borderWidth: 0,
  };
  return StyleSheet.create({
    scroll: {
      flex: 1,
    },
    scrollContent: {
      paddingBottom: STACK_SECTION_GAP,
    },
    emptyState: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: appTheme.spacing.xl,
    },
    emptyStateText: {
      fontSize: 16,
      color: c.textSecondary,
      textAlign: 'center',
    },
    imageContainer: {
      width: SCREEN_WIDTH,
      height: SCREEN_WIDTH * 0.85,
      backgroundColor: c.surface,
      position: 'relative',
    },
    image: {
      width: '100%',
      height: '100%',
    },
    imagePlaceholder: {
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: c.background,
    },
    detailsContainer: {
      marginHorizontal: STACK_SCROLL_GUTTER,
      marginTop: -28,
      padding: DASH_STUDIO_INNER,
      minHeight: SCREEN_WIDTH * 0.5,
      ...studio_card,
    },
    titleSection: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: appTheme.spacing.sm,
    },
    itemName: {
      flex: 1,
      fontSize: 26,
      fontWeight: '700',
      color: c.text,
      letterSpacing: -0.5,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    categoryBadge: {
      backgroundColor: c.primary + '15',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
      marginStart: appTheme.spacing.sm,
    },
    categoryText: {
      fontSize: 12,
      fontWeight: '600',
      color: c.primary,
    },
    description: {
      fontSize: 15,
      color: c.textSecondary,
      lineHeight: 24,
      marginBottom: appTheme.spacing.xl,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: c.text,
      marginBottom: appTheme.spacing.md,
      letterSpacing: -0.3,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    nutritionContainer: {
      marginBottom: appTheme.spacing.xl,
    },
    nutritionGrid: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 10,
      ...horizontalLtrDirectionStyle,
    },
    nutritionCard: {
      flex: 1,
      padding: 10,
      alignItems: 'center',
      ...studio_card,
      borderRadius: 16,
    },
    nutritionLabel: {
      fontSize: 10,
      color: c.textSecondary,
      fontWeight: '600',
      marginBottom: 4,
      textAlign: 'center',
    },
    nutritionValue: {
      fontSize: 15,
      fontWeight: '700',
      color: c.text,
    },
    nutritionUnit: {
      fontSize: 10,
      color: c.textSecondary,
      fontWeight: '500',
    },
    nutritionCitationStrip: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: appTheme.spacing.sm,
      marginTop: appTheme.spacing.md,
      padding: appTheme.spacing.md,
      ...studio_card,
      backgroundColor: c.surfaceLight,
      borderRadius: appTheme.borderRadius.md,
    },
    nutritionCitationTextWrap: {
      flex: 1,
      minWidth: 0,
    },
    nutritionCitationText: {
      ...appTheme.typography.bodySmall,
      fontSize: 12,
      lineHeight: 18,
      color: c.textSecondary,
    },
    nutritionCitationLink: {
      ...appTheme.typography.bodySmall,
      fontSize: 13,
      fontWeight: '700',
      color: c.primary,
      marginTop: appTheme.spacing.xs,
    },
    ingredientsContainer: {
      marginBottom: appTheme.spacing.xl,
    },
    ingredientsList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    ingredientChip: {
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: 20,
      backgroundColor: c.surfaceLight ?? c.surface,
    },
    ingredientText: {
      fontSize: 13,
      color: c.text,
      fontWeight: '500',
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    recipeText: {
      fontSize: 15,
      color: c.textSecondary,
      lineHeight: 24,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    warningContainer: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      backgroundColor: c.error + '10',
      padding: appTheme.spacing.md,
      borderRadius: 16,
      marginBottom: appTheme.spacing.lg,
    },
    warningIconContainer: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: c.error + '20',
      justifyContent: 'center',
      alignItems: 'center',
      marginEnd: appTheme.spacing.sm,
    },
    warningContent: {
      flex: 1,
    },
    warningTitle: {
      fontSize: 14,
      fontWeight: '700',
      color: c.error,
      marginBottom: 2,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    warningText: {
      fontSize: 13,
      color: c.error,
      lineHeight: 18,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    bottomSpacer: {
      height: 20,
    },
    footer: {
      backgroundColor: c.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderLight,
      ...appTheme.shadows.lg,
    },
    footerContent: {
      paddingHorizontal: appTheme.spacing.lg,
      paddingTop: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.sm,
    },
  });
}

export const FoodItemDetailScreen: React.FC = () => {
  const menu_flow_variant = useMenuFlowVariant();
  if (menu_flow_variant === 'set2') {
    return <FoodItemDetailScreenSet2 />;
  }
  return <FoodItemDetailScreenClassic />;
};

function FoodItemDetailScreenClassic() {
  useIosStatusBarStyle('light');
  const { t } = useTranslation();
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const insets = useSafeAreaInsets();
  const route = useRoute<FoodItemDetailRouteProp>();
  const name_fallback = t('foodItemDetail.foodItemFallback');
  const normalized = useMemo(() => normalizeItem(route.params, name_fallback), [route.params, name_fallback]);

  useEffect(() => {
    if (!__DEV__) {
      return;
    }
    const organization = getOrganization();
    const raw_item = route.params?.item;
    console.log('[FoodItemDetail] organization (initial config):', organization);
    console.log('[FoodItemDetail] menu item (route params):', raw_item);
    console.log('[FoodItemDetail] show_actual_recipes:', organization?.show_actual_recipes);
  }, [route.params?.item]);

  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();
  const styles = useMemo(() => createFoodItemDetailStyles(colors, rtl), [colors, rtl]);
  const primary_footer = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(colors),
      label: createStackedFooterPrimaryLabelStyle(colors),
    }),
    [colors],
  );

  const nutritionData = useMemo(() => {
    if (!normalized) {
      return [];
    }
    const fmt = (v: number) => (Number.isFinite(v) ? formatDisplayDecimal(v, 1) : formatDisplayDecimal(0, 1));
    return [
      { label: t('foodItemDetail.cal'), value: fmt(normalized.calories), unit: 'kcal' },
      { label: t('foodItemDetail.protein'), value: fmt(normalized.protein), unit: 'g' },
      { label: t('foodItemDetail.carbs'), value: fmt(normalized.carbs), unit: 'g' },
      { label: t('foodItemDetail.fats'), value: fmt(normalized.fats), unit: 'g' },
    ];
  }, [normalized, t]);

  if (!normalized) {
    return (
      <StackScreenShell colors={colors}>
        {Platform.OS === 'android' ? <StatusBar barStyle="light-content" /> : null}
        <ScreenHeader titleMode="inline" title="" onBack={on_back_exit} />
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>{t('foodItemDetail.noItemData')}</Text>
        </View>
      </StackScreenShell>
    );
  }

  const item = normalized;

  return (
    <StackScreenShell colors={colors}>
      {Platform.OS === 'android' ? <StatusBar barStyle="light-content" /> : null}
      <ScreenHeader titleMode="inline" title="" onBack={on_back_exit} />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + STACK_SECTION_GAP }]}
        showsVerticalScrollIndicator={false}
        bounces={false}
        keyboardShouldPersistTaps="handled">
        <View style={styles.imageContainer}>
          {item.imageUri ? (
            <Image source={{ uri: item.imageUri }} style={styles.image} resizeMode="cover" />
          ) : (
            <View style={styles.imagePlaceholder}>
              <SvgIcon name="menu" size={80} color={colors.borderLight} />
            </View>
          )}
        </View>

        <View style={styles.detailsContainer}>
          <View style={styles.titleSection}>
            <Text style={styles.itemName}>{getApiName(item as Record<string, unknown>) || item.name}</Text>
            {item.food_type_category ? (
              <View style={styles.categoryBadge}>
                <Text style={styles.categoryText}>{String(item.food_type_category).toLowerCase() === 'meal' ? t('foodItemDetail.meal') : t('foodItemDetail.snack')}</Text>
              </View>
            ) : null}
          </View>

          {(getApiText(item as Record<string, unknown>, 'description', 'a_description') || item.description) ? (
            <Text style={styles.description}>{getApiText(item as Record<string, unknown>, 'description', 'a_description') || item.description}</Text>
          ) : null}

          <View style={styles.nutritionContainer}>
            <Text style={styles.sectionTitle}>{t('foodItemDetail.nutritionalInformation')}</Text>
            <View style={styles.nutritionGrid}>
              {nutritionData.map((nutrient, index) => (
                <View key={index} style={styles.nutritionCard}>
                  <Text style={styles.nutritionLabel}>{nutrient.label}</Text>
                  <View style={horizontalLtrDirectionStyle}>
                    <Text>
                      <Text style={styles.nutritionValue}>{nutrient.value}</Text>
                      <Text style={styles.nutritionUnit}> {nutrient.unit}</Text>
                    </Text>
                  </View>
                </View>
              ))}
            </View>
            <HealthInformationCmsGate>
            <View style={styles.nutritionCitationStrip}>
              <SvgIcon name="medkit" size={18} color={colors.primary} />
              <View style={styles.nutritionCitationTextWrap}>
                <Text style={styles.nutritionCitationText}>{t('foodItemDetail.nutritionReferencesFootnote')}</Text>
                <Clickable
                  onPress={() => (navigation as { navigate: (name: 'HealthInformationSources') => void }).navigate('HealthInformationSources')}
                  accessibilityRole="link"
                  accessibilityLabel={t('foodItemDetail.viewNutritionReferences')}
                  hitSlop={appTheme.hitSlop.small}>
                  <Text style={styles.nutritionCitationLink}>{t('foodItemDetail.viewNutritionReferences')}</Text>
                </Clickable>
              </View>
            </View>
            </HealthInformationCmsGate>
          </View>

          <FoodItemRecipeSection item={route.params?.item as Record<string, unknown>} styles={styles} />

          {item.health_code != null && item.health_code >= 10 ? (
            <View style={styles.warningContainer}>
              <View style={styles.warningIconContainer}>
                <SvgIcon name="alert" size={18} color={colors.error} />
              </View>
              <View style={styles.warningContent}>
                <Text style={styles.warningTitle}>{t('foodItemDetail.healthAlert')}</Text>
                <Text style={styles.warningText}>{item.health_code === 10 ? t('foodItemDetail.containsAllergens') : t('foodItemDetail.containsDisliked')}</Text>
              </View>
            </View>
          ) : null}

          <View style={styles.bottomSpacer} />
        </View>
      </ScrollView>

      <SafeAreaView edges={['bottom']} style={styles.footer}>
        <View style={styles.footerContent}>
          <Clickable onPress={on_back_exit} accessibilityLabel={t('foodItemDetail.back')} accessibilityRole="button" contentStyle={{ alignSelf: 'stretch' }}>
            <View style={[primary_footer.surface, { justifyContent: 'center', minHeight: 52 }]}>
              <Text style={primary_footer.label}>{t('foodItemDetail.back')}</Text>
            </View>
          </Clickable>
        </View>
      </SafeAreaView>
    </StackScreenShell>
  );
};

export default FoodItemDetailScreen;
